'use client';

import Link from 'next/link';
import { useActionState } from 'react';

import { TurnstileWidget } from '@/components/security/turnstile-widget';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { signInAction, signUpAction, type AuthState } from './actions';

const initialState: AuthState = { error: null, message: null };

export function LoginForm({
  redirectTo,
  turnstileSiteKey,
}: {
  redirectTo: string;
  turnstileSiteKey: string | null;
}) {
  const [signInState, signInDispatch, signInPending] = useActionState(signInAction, initialState);
  const [signUpState, signUpDispatch, signUpPending] = useActionState(signUpAction, initialState);

  const pending = signInPending || signUpPending;
  const error = signInState.error ?? signUpState.error;
  const message = signInState.message ?? signUpState.message;

  return (
    <form className="space-y-4">
      <input type="hidden" name="redirect" value={redirectTo} />

      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          disabled={pending}
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-baseline justify-between">
          <Label htmlFor="password">Password</Label>
          <Link
            href="/forgot-password"
            className="text-xs text-neutral-500 underline-offset-4 hover:underline"
          >
            Forgot password?
          </Link>
        </div>
        <Input
          id="password"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          disabled={pending}
        />
      </div>

      <TurnstileWidget siteKey={turnstileSiteKey} />

      {error && <p className="text-sm text-red-600">{error}</p>}
      {message && <p className="text-sm text-emerald-600">{message}</p>}

      <div className="grid grid-cols-2 gap-2">
        <Button type="submit" formAction={signInDispatch} variant="brand" disabled={pending}>
          {signInPending ? 'Signing in…' : 'Sign in'}
        </Button>
        <Button type="submit" formAction={signUpDispatch} variant="outline" disabled={pending}>
          {signUpPending ? 'Registering…' : 'Register'}
        </Button>
      </div>
    </form>
  );
}
